/**
 * useNotificationEvents — subscribe the signed-in user's `notifications:{id}`
 * channel (WP-E).
 *
 * This channel has existed, and been authorized, since the realtime substrate
 * shipped: `notificationChannel()` was exported and `apis/realtime`'s policy
 * has always allowed `notifications:*` for an authenticated caller. Nothing
 * ever subscribed it, so the per-user events the server published reached
 * nobody, and the Messages tab badge only refreshed while a conversation
 * thread happened to be open — the one place a `conversations:{id}`
 * subscription existed.
 *
 * Subscribing is the whole fix. The provider already mounts the central
 * envelope bridge (`useRealtimeQueryBridge`), which applies the declared
 * query invalidations for EVERY envelope on ANY subscribed channel — so once
 * this channel is live, `conversationMessageReceived: invalidateUnreadCount`
 * fires for messages that arrive while the user is anywhere in the app.
 *
 * Note on what the envelope carries (WP-C): `unread_count` on this channel is
 * PER-CONVERSATION. The tab badge counts CONVERSATIONS WITH UNREAD. They are
 * different numbers and must never be assigned to one another, which is why
 * this hook invalidates the badge query rather than writing a value into it.
 *
 * ## The inbox catch-up (#8749)
 *
 * Envelopes are delivered only while the channel is subscribed, and the wire
 * has no replay — so every message that landed in ANY conversation while the
 * socket was down (or before the first `subscribed` frame) moved no badge and
 * no sidebar row. Until #8749 the thread page papered over that for the open
 * thread's route by re-running its whole loader on every subscribe, which
 * re-read `ListConversations` and folded it into the list store; the thread's
 * catch-up is now a delta of ITS messages only, so the inbox needs its own.
 *
 * The owner is this channel: it is the per-user stream the inbox surfaces are
 * a function of, and it is subscribed on every page, not just a thread. So on
 * every false→true of THIS subscription — the first included, matching the
 * thread's #8651 rule — the two inbox queries are invalidated, exactly as an
 * envelope would have invalidated them ({@link invalidateInboxQueries}, the
 * one statement of which keys those are).
 */
import { invalidateInboxQueries } from '@/shared/lib/realtime-query-bindings';
import { notificationChannel, useSubscription } from '@letsgethome/realtime';
import { useQueryClient } from '@tanstack/react-query';
import * as React from 'react';

import { useAuth } from '../providers/AuthProvider';

/**
 * Subscribe the authenticated user's notification channel.
 *
 * A no-op while signed out: a signed-out user has no notification channel, so
 * the hook passes `null` (H-4) — `useSubscription` opens nothing and requests
 * no token. No empty-string channel is invented.
 */
export function useNotificationEvents(): { isSubscribed: boolean } {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const subscription = useSubscription(
    user ? notificationChannel(user.id) : null,
    // The envelope needs no per-message handling here: the provider's bridge
    // applies the declared invalidations centrally. Subscribing is what makes
    // those bindings fire at all.
    () => {}
  );
  const { isSubscribed } = subscription;

  React.useEffect(() => {
    // Every false→true is "envelopes flow from NOW"; whatever the inbox missed
    // before it is re-read, not replayed. `isSubscribed` starts `false`, so the
    // first `subscribed` frame is a transition like any other.
    if (!isSubscribed) return;
    invalidateInboxQueries(queryClient);
  }, [isSubscribed, queryClient]);

  return subscription;
}
